import * as React from 'react';

import Service from '../../lib/App';
import { Vehicle } from '../../lib/PlannerMetadata';
import InputControlProps from '../InputControlProps';
import { CitySearchTreeData, getLabeledCitiesArray } from './CityTreeSelect';
import CommonTreeSelect, {
  createGroupedArray,
  getFilteredChildrenAndCount,
  SearchableTreeData,
  TreeCountAndChildren,
  TreeData,
} from './CommonTreeSelect';

interface LabeledCitiesMap {
  [cityKey: string]: CitySearchTreeData;
}

/**
 * This is a helper method, which generates object (map - key/value) which will
 * allow easily to get info about the city by cityId.
 *
 * @return { LabeledCitiesMap } map - js object, where keys are our city ids
 * and values are objects with info about cities { @see CitySearchTreeData }
 */
const getCitiesMap = (): LabeledCitiesMap =>
  getLabeledCitiesArray().reduce(
    (
      map: LabeledCitiesMap,
      labeledCity: CitySearchTreeData,
    ): LabeledCitiesMap => {
      map[labeledCity.key] = labeledCity;
      return map;
    },
    {},
  );

interface SearchableCityTreeData extends SearchableTreeData {
  serviceType: string;
  cityID: number;
}

export class VehicleTreeSelect extends React.Component<InputControlProps> {
  citiesMap: LabeledCitiesMap;

  constructor(props) {
    super(props);

    this.citiesMap = getCitiesMap();
  }

  getValuesForSingleParentCase = (values: string, search: string): string[] => {
    if (values.includes('_')) {
      const [_serviceType, _cityID]: string[] = values.split('_');
      const treeData: TreeData[] = this.getTreeData(search);
      const serviceType: string = `serviceType_${_serviceType}`;

      const filteredTreeData: TreeData[] = treeData!.find(
        ({ key }: TreeData): boolean => key === serviceType,
      )!.children!;

      if (_cityID) {
        return filteredTreeData
          .find(({ key }: TreeData): boolean => key === values)!
          .children!.map(({ key }: TreeData): string => String(key));
      } else {
        return filteredTreeData![0].children!.map(({ key }: TreeData): string =>
          String(key),
        );
      }
    } else {
      return [values];
    }
  };

  /**
   * @see CommonTreeSelectProps.getTreeData
   *
   * VehicleTreeSelect has custom hierarchy for grouping - it has 2 levels of grouping
   * by cityID and by ServiceType, note please that parent nodes (city nodes and
   * service type nodes) have custom values that contains underscore '_'. Why?
   *
   * This underscore later is used in here in getValuesForSingleParentCase.
   * Also, for multiple tree select mode, 2nd level (cities in here) should have unique id,
   * otherwise selecting city on one type, we will select same in all types.
   */
  getTreeData = (search: string): TreeData[] => {
    search = search.toLowerCase();

    return createGroupedArray(
      this.getAllValues(),
      'serviceType',
      (serviceType: string, vehicles: SearchableTreeData[]): TreeData => {
        const { count }: TreeCountAndChildren = getFilteredChildrenAndCount(
          vehicles,
          search,
        );

        return {
          title: `${serviceType} ${count}`,
          value: `serviceType_${serviceType}`,
          key: `serviceType_${serviceType}`,
          selectable: parseInt(count, 10) > 1 && Boolean(this.props.multiple),
          // Note that setting in here vehicles, not filtered children,
          // because want to count also on layer deeper
          children: createGroupedArray(
            vehicles,
            'cityID',
            (cityID: string, vehicles: SearchableTreeData[]): TreeData => {
              const { count, children }: TreeCountAndChildren =
                getFilteredChildrenAndCount(vehicles, search);
              const customCityKey: string = `city_${serviceType}_${cityID}`;

              return {
                title: (
                  <span>
                    {this.citiesMap[cityID]
                      ? this.citiesMap[cityID].key
                      : 'None'}{' '}
                    {count}
                  </span>
                ),
                // key: customCityKey,
                // if we leave just cityID - selecting city - will select it in all service types
                // so we need custom "primary_key" in here)
                key: customCityKey,
                value: customCityKey,
                children,
                selectable:
                  parseInt(count, 10) > 1 && Boolean(this.props.multiple),
              };
            },
          ),
        };
      },
    );
  };

  getAllValues = (): SearchableTreeData[] =>
    Service.cache!.getAllVehicles().map(
      ({ id, name, serviceType, cityID }: Vehicle): SearchableCityTreeData => {
        const title: string = `${id}: ${name} (${serviceType})`;
        const value: string = `${id}`;
        return {
          title,
          search: title.toLowerCase(),
          key: value,
          value,
          serviceType,
          cityID,
        };
      },
    );

  render() {
    const { getValuesForSingleParentCase, getTreeData } = this;
    let placeholder: string | undefined = this.props.placeholder;
    if (!placeholder) {
      placeholder = `Please select service${this.props.multiple ? 's' : ''}`;
    }

    return (
      <CommonTreeSelect
        {...{
          ...this.props,
          allValues: this.getAllValues(),
          placeholder,
          getValuesForSingleParentCase,
          getTreeData,
        }}
      />
    );
  }
}

export default VehicleTreeSelect;
